import { Link, useLocation } from "react-router-dom";
import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import Img1 from "../../assets/img/gallery/logo1.png";
import { HREFLANG, switchHref, linkTo } from "../../i18n/lang.mjs";
import { WIGS_ENABLED } from "../../config/features.mjs";

/* ─────────────────────────────────────────────
   DESIGN TOKENS
   ───────────────────────────────────────────── */
const BRAND   = "#90510F";
const DARK    = "#1A0D05";
const MUTED   = "#8B7B6B";
const WARM    = "#5a3a20";

const NAV_LINK = {
  fontFamily: "'Inter','Roboto',sans-serif",
  fontSize: "12px",
  fontWeight: "600",
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  color: DARK,
  textDecoration: "none",
  padding: "6px 0",
  position: "relative",
  transition: "color 0.3s",
  whiteSpace: "nowrap",
};
const NAV_LINK_ACTIVE = { ...NAV_LINK, color: BRAND };

const SOON_BADGE = {
  display: "inline-flex",
  alignItems: "center",
  padding: "2px 6px",
  background: "rgba(144,81,15,0.10)",
  border: "1px solid rgba(144,81,15,0.22)",
  borderRadius: "9999px",
  fontFamily: "'Inter',sans-serif",
  fontSize: "8px",
  fontWeight: "700",
  letterSpacing: "0.13em",
  color: BRAND,
  textTransform: "uppercase",
  lineHeight: 1,
  userSelect: "none",
};

const MOBILE_LINK = {
  fontFamily: "'Inter','Roboto',sans-serif",
  fontSize: "15px",
  fontWeight: "600",
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  color: DARK,
  textDecoration: "none",
  display: "block",
  padding: "4px 0",
  transition: "color 0.25s",
};
const MOBILE_SUB_LINK = {
  ...MOBILE_LINK,
  fontSize: "13px",
  fontWeight: "500",
  letterSpacing: "0.06em",
  color: WARM,
};

/* ─────────────────────────────────────────────
   FLAG SVGs
   Inline SVG ensures correct colors on every
   browser / OS, including Windows 10 where
   emoji flags render as letter codes.
   ───────────────────────────────────────────── */

/**
 * Serbian civil flag - three equal horizontal bands:
 *   red (#C6363C) / blue (#0C4076) / white (#FFFFFF)
 * Aspect ratio 3∶2
 */
const SerbiaFlag = ({ height = 18 }) => (
  <svg
    width={Math.round(height * 1.5)}
    height={height}
    viewBox="0 0 3 2"
    xmlns="http://www.w3.org/2000/svg"
    style={{ display: "block", borderRadius: "2px", flexShrink: 0 }}
    aria-hidden="true"
    focusable="false"
  >
    <rect width="3" height="0.6667" fill="#C6363C" />
    <rect y="0.6667" width="3" height="0.6667" fill="#0C4076" />
    <rect y="1.3333" width="3" height="0.6667" fill="#FFFFFF" />
  </svg>
);

/**
 * United Kingdom flag (Union Jack)
 * Official colours: Union Blue #012169, Union Red #C8102E, White #FFFFFF
 * Aspect ratio 2∶1
 * Layers (back to front):
 *   1. Blue field
 *   2. White saltire  (St Andrew / St Patrick combined, width ≈ ⅓ of height)
 *   3. Red saltire    (St Patrick approximation, width ≈ ⅛ of height)
 *   4. White fimbriation for St George's cross
 *   5. Red St George's cross
 */
/**
 * German flag - three equal horizontal bands:
 *   black (#000000) / red (#DD0000) / gold (#FFCE00)
 * Aspect ratio 3∶2
 */
const GermanyFlag = ({ height = 18 }) => (
  <svg
    width={Math.round(height * 1.5)}
    height={height}
    viewBox="0 0 3 2"
    xmlns="http://www.w3.org/2000/svg"
    style={{ display: "block", borderRadius: "2px", flexShrink: 0 }}
    aria-hidden="true"
    focusable="false"
  >
    <rect width="3" height="0.6667" fill="#000000" />
    <rect y="0.6667" width="3" height="0.6667" fill="#DD0000" />
    <rect y="1.3333" width="3" height="0.6667" fill="#FFCE00" />
  </svg>
);

const UKFlag = ({ height = 18 }) => (
  <svg
    width={height * 2}
    height={height}
    viewBox="0 0 60 30"
    xmlns="http://www.w3.org/2000/svg"
    style={{ display: "block", borderRadius: "2px", flexShrink: 0 }}
    aria-hidden="true"
    focusable="false"
  >
    {/* 1 · Blue field */}
    <rect width="60" height="30" fill="#012169" />

    {/* 2 · White saltire - diagonal stripes */}
    <path
      d="M0,0 L60,30 M60,0 L0,30"
      stroke="#FFFFFF"
      strokeWidth="10"
      strokeLinecap="butt"
    />

    {/* 3 · Red saltire - thinner, centred on white */}
    <path
      d="M0,0 L60,30 M60,0 L0,30"
      stroke="#C8102E"
      strokeWidth="4"
      strokeLinecap="butt"
    />

    {/* 4 · White fimbriation for St George's cross
          Width = 2/5 of height (12 units), centred at x=30 and y=15 */}
    <rect x="24" y="0" width="12" height="30" fill="#FFFFFF" />
    <rect x="0"  y="9" width="60" height="12" fill="#FFFFFF" />

    {/* 5 · Red St George's cross
          Width = 1/5 of height (6 units), centred at x=30 and y=15 */}
    <rect x="27" y="0" width="6" height="30" fill="#C8102E" />
    <rect x="0" y="12" width="60" height="6"  fill="#C8102E" />
  </svg>
);

/* ─────────────────────────────────────────────
   LANGUAGE SWITCHER
   Flags only - no text labels or abbreviations.
   compact = smaller padding (mobile / tablet).
   ───────────────────────────────────────────── */
const LANGS = [
  { code: "sr", Flag: SerbiaFlag,  title: "Srpski"  },
  { code: "en", Flag: UKFlag,      title: "English" },
  { code: "de", Flag: GermanyFlag, title: "Deutsch" },
];

const LanguageSwitcher = ({ compact = false }) => {
  const { i18n } = useTranslation();
  const location = useLocation();
  const raw     = i18n.language || "sr";
  const current = LANGS.find(l => raw.startsWith(l.code))?.code ?? "sr";
  const flagH   = compact ? 15 : 17;

  // useLocation() is basename-relative, so pathname is the logical route
  // (without the /en, /de prefix). Each link points to the SAME page in the
  // target language. Real <a href> so Googlebot can crawl every version.
  const logical = location.pathname;

  return (
    <div
      role="group"
      aria-label="Language selector"
      style={{
        display: "flex",
        alignItems: "center",
        background: "rgba(144,81,15,0.06)",
        border: "1px solid rgba(144,81,15,0.18)",
        borderRadius: "9999px",
        padding: "3px",
        gap: "2px",
        flexShrink: 0,
      }}
    >
      {LANGS.map(({ code, Flag, title }) => {
        const active = current === code;
        return (
          <a
            key={code}
            href={switchHref(logical, code)}
            hrefLang={HREFLANG[code]}
            onClick={() => localStorage.setItem("vb_lang", code)}
            title={title}
            aria-label={title}
            aria-current={active ? "true" : undefined}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: compact ? "4px 5px" : "5px 7px",
              borderRadius: "9999px",
              border: "none",
              cursor: "pointer",
              textDecoration: "none",
              background: active ? BRAND : "transparent",
              boxShadow: active ? "0 2px 8px rgba(144,81,15,0.24)" : "none",
              opacity: active ? 1 : 0.55,
              transition: "background 0.22s, box-shadow 0.22s, opacity 0.22s",
              flexShrink: 0,
              lineHeight: 0,
            }}
            onMouseEnter={e => { if (!active) e.currentTarget.style.opacity = "0.9"; }}
            onMouseLeave={e => { if (!active) e.currentTarget.style.opacity = "0.55"; }}
          >
            <Flag height={flagH} />
          </a>
        );
      })}
    </div>
  );
};

/* ─────────────────────────────────────────────
   MAIN NAVBAR
   Desktop nav: visible at xl (≥ 1200 px).
   Hamburger: shown below xl.
   ───────────────────────────────────────────── */
const Main = () => {
  const { t, i18n } = useTranslation();
  const [mobileOpen,   setMobileOpen]   = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [eduOpen,      setEduOpen]      = useState(false);
  const [eduHover,     setEduHover]     = useState(false);
  const [scrolled,     setScrolled]     = useState(false);
  const location = useLocation();
  const lang = ["sr", "en", "de"].includes(i18n.language) ? i18n.language : "sr";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setServicesOpen(false);
    setEduOpen(false);
    setEduHover(false);
  }, [location]);

  const isActive = (path) => location.pathname === path;

  /* "Edukacije" is a parent item: the basic course keeps /educations, the
     advanced one has its own page under a per-language slug. */
  const educationSubLinks = [
    { to: "/educations",                            label: t("nav.basicCourse")    },
    { to: linkTo("/napredni-kurs-sminkanja", lang), label: t("nav.advancedCourse") },
  ];

  const desktopLinks = [
    { to: "/",           label: t("nav.home")       },
    { to: "/service",    label: t("nav.services")    },
    { to: "/pricing",    label: t("nav.pricing")     },
    { to: "/giftcard",   label: t("nav.giftcard")    },
    { to: "/educations", label: t("nav.educations"), sub: educationSubLinks },
    { to: linkTo("/ponude", lang), label: t("nav.offers") },
    { to: "/jobs",       label: t("nav.jobs")        },
    { to: "/gallery",    label: t("nav.gallery")     },
    { to: "/blog",       label: t("nav.blog")        },
    { to: "/contact",    label: t("nav.contact")     },
  ];

  const mobileLinks = [
    { to: "/",           label: t("nav.home")       },
    { to: "/pricing",    label: t("nav.pricing")    },
    { to: "/giftcard",   label: t("nav.giftcard")   },
    { to: "/educations", label: t("nav.educations"), sub: educationSubLinks },
    { to: linkTo("/ponude", lang), label: t("nav.offers") },
    { to: "/jobs",       label: t("nav.jobs")       },
    { to: "/gallery",    label: t("nav.gallery")    },
    { to: "/blog",       label: t("nav.blog")       },
    { to: "/contact",    label: t("nav.contact")    },
  ];

  const serviceSubLinks = [
    { to: "/makeup",           label: t("nav.svcMakeup")          },
    { to: "/permanent-makeup", label: t("nav.svcPermanentMakeup") },
    { to: "/vega",             label: t("nav.svcVega")            },
    { to: "/baby-lips",        label: t("nav.svcBabyLips")        },
    { to: "/eyelashes",        label: t("nav.svcEyelashes")       },
    { to: "/nails",            label: t("nav.svcNails")           },
    { to: "/depilation",       label: t("nav.svcDepilation")      },
    { to: "/hairdresser",      label: t("nav.svcHairdresser")     },
    // Hair sub-pages. Those with a translated slug go through linkTo().
    { to: linkTo("/farbanje-kose", lang),      label: t("nav.svcColouring")   },
    { to: "/balayage",                         label: t("nav.svcBalayage")    },
    { to: linkTo("/tretmani-kose", lang),      label: t("nav.svcTreatments")  },
    { to: linkTo("/musko-sisanje", lang),      label: t("nav.svcMensHaircut") },
    { to: linkTo("/ekstenzije-za-kosu", lang), label: t("nav.svcExtensions")  },
    // Wigs are not in stock yet - see src/config/features.mjs.
    ...(WIGS_ENABLED ? [{ to: linkTo("/perike", lang), label: t("nav.svcWigs") }] : []),
  ];

  return (
    <>
      <style>{`
        @keyframes mobileMenuIn {
          from { opacity:0; transform:translateY(-8px); }
          to   { opacity:1; transform:translateY(0); }
        }
        .vb-nl:hover { color:${BRAND} !important; }
        .vb-nl:hover .vb-nl-bar { width:100% !important; }
      `}</style>

      <header
        style={{
          position: "fixed",
          inset: "0 0 auto 0",
          zIndex: 9999,
          background: "rgba(255,255,255,0.97)",
          backdropFilter: "saturate(180%) blur(20px)",
          WebkitBackdropFilter: "saturate(180%) blur(20px)",
          boxShadow: scrolled
            ? "0 4px 32px rgba(0,0,0,0.11)"
            : "0 1px 0 rgba(144,81,15,0.10)",
          borderBottom: `1px solid ${scrolled ? "rgba(144,81,15,0.14)" : "rgba(144,81,15,0.08)"}`,
          transition: "box-shadow 0.4s, border-color 0.4s",
          WebkitTransform: "translateZ(0)",
        }}
      >
        {/* ── Inner container ─────────────────── */}
        <div
          style={{
            maxWidth: "1400px",
            margin: "0 auto",
            padding: "0 24px",
            display: "flex",
            alignItems: "center",
            height: "72px",
          }}
        >

          {/* LOGO */}
          <div style={{ flexShrink: 0, marginRight: "32px" }}>
            <Link to="/" style={{ display: "block", lineHeight: 0 }}>
              <img
                src={Img1}
                alt="Vision Beauty"
                width="499"
                height="500"
                style={{ height: "56px", width: "auto", display: "block" }}
              />
            </Link>
          </div>

          {/* DESKTOP NAV - xl (≥ 1200 px) */}
          <nav
            aria-label="Main navigation"
            className="d-none d-xl-flex"
            style={{
              flex: "1 1 auto",
              minWidth: 0,
              overflow: "hidden",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <ul
              style={{
                display: "flex",
                alignItems: "center",
                gap: "20px",
                margin: 0,
                padding: 0,
                listStyle: "none",
                flexWrap: "nowrap",
              }}
            >
              {desktopLinks.map(({ to, label, sub }) => {
                // A parent item stays a real link to its own page and only
                // adds a hover menu, so the top-level page is still reachable
                // (and crawlable) in one click.
                const active = isActive(to) || (sub || []).some(s => isActive(s.to));
                return (
                  <li
                    key={to}
                    style={{ flexShrink: 0, position: sub ? "relative" : undefined }}
                    onMouseEnter={sub ? () => setEduHover(true) : undefined}
                    onMouseLeave={sub ? () => setEduHover(false) : undefined}
                  >
                    <Link
                      to={to}
                      className="vb-nl"
                      style={active ? NAV_LINK_ACTIVE : NAV_LINK}
                    >
                      {label}
                      <span
                        className="vb-nl-bar"
                        style={{
                          position: "absolute",
                          bottom: 0,
                          left: 0,
                          height: "2px",
                          width: active ? "100%" : "0",
                          background: BRAND,
                          borderRadius: "99px",
                          transition: "width 0.3s",
                        }}
                      />
                    </Link>

                    {sub && eduHover && (
                      <ul
                        style={{
                          position: "absolute",
                          top: "100%",
                          left: "50%",
                          transform: "translateX(-50%)",
                          minWidth: "210px",
                          margin: 0,
                          padding: "8px",
                          listStyle: "none",
                          background: "#fff",
                          borderRadius: "14px",
                          border: "1px solid rgba(144,81,15,0.14)",
                          boxShadow: "0 16px 40px rgba(0,0,0,0.14)",
                          animation: "mobileMenuIn 0.18s ease both",
                        }}
                      >
                        {sub.map(({ to: subTo, label: subLabel }) => (
                          <li key={subTo}>
                            <Link
                              to={subTo}
                              style={{
                                display: "block",
                                padding: "10px 14px",
                                borderRadius: "10px",
                                fontFamily: "'Inter','Roboto',sans-serif",
                                fontSize: "13.5px",
                                fontWeight: 600,
                                whiteSpace: "nowrap",
                                textDecoration: "none",
                                color: isActive(subTo) ? BRAND : DARK,
                              }}
                              onMouseEnter={e => (e.currentTarget.style.background = "rgba(144,81,15,0.07)")}
                              onMouseLeave={e => (e.currentTarget.style.background = "transparent")}
                            >
                              {subLabel}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                );
              })}
            </ul>
          </nav>

          {/* DESKTOP RIGHT: Book Appointment + Language - xl */}
          <div
            className="d-none d-xl-flex"
            style={{
              flexShrink: 0,
              marginLeft: "24px",
              alignItems: "center",
              gap: "12px",
            }}
          >
            <Link
              to="/contact"
              style={{
                fontFamily: "'Inter','Roboto',sans-serif",
                fontSize: "11.5px",
                fontWeight: "700",
                letterSpacing: "0.11em",
                textTransform: "uppercase",
                background: BRAND,
                color: "#fff",
                padding: "11px 20px",
                borderRadius: "9999px",
                textDecoration: "none",
                whiteSpace: "nowrap",
                boxShadow: "0 6px 20px rgba(144,81,15,0.28)",
                transition: "background 0.3s, transform 0.2s, box-shadow 0.3s",
                display: "inline-block",
              }}
              onMouseEnter={e => {
                e.currentTarget.style.background  = "#6b3b08";
                e.currentTarget.style.transform   = "translateY(-2px)";
                e.currentTarget.style.boxShadow   = "0 10px 28px rgba(144,81,15,0.40)";
              }}
              onMouseLeave={e => {
                e.currentTarget.style.background  = BRAND;
                e.currentTarget.style.transform   = "translateY(0)";
                e.currentTarget.style.boxShadow   = "0 6px 20px rgba(144,81,15,0.28)";
              }}
            >
              {t("nav.bookAppointment")}
            </Link>

            {/* Flag-only switcher - desktop */}
            <LanguageSwitcher />
          </div>

          {/* MOBILE / TABLET: Language + Burger - below xl */}
          <div
            className="d-xl-none"
            style={{
              marginLeft: "auto",
              display: "flex",
              alignItems: "center",
              gap: "10px",
            }}
          >
            {/* Flag-only switcher - compact */}
            <LanguageSwitcher compact />

            {/* Burger */}
            <button
              aria-label={mobileOpen ? t("nav.closeMenu") : t("nav.openMenu")}
              aria-expanded={mobileOpen}
              onClick={() => setMobileOpen(v => !v)}
              style={{
                width: "44px",
                height: "44px",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                gap: "5px",
                background: "#fff",
                borderRadius: "12px",
                border: "1px solid rgba(144,81,15,0.20)",
                boxShadow: "0 2px 12px rgba(0,0,0,0.09)",
                cursor: "pointer",
                padding: 0,
                flexShrink: 0,
              }}
            >
              {[0, 1, 2].map(i => (
                <span
                  key={i}
                  style={{
                    display: "block",
                    width: "20px",
                    height: "2px",
                    background: BRAND,
                    borderRadius: "9999px",
                    transition: "transform 0.3s, opacity 0.3s",
                    transform: mobileOpen
                      ? i === 0 ? "translateY(7px) rotate(45deg)"
                      : i === 2 ? "translateY(-7px) rotate(-45deg)"
                      : "scaleX(0)"
                      : "none",
                    opacity: mobileOpen && i === 1 ? 0 : 1,
                  }}
                />
              ))}
            </button>
          </div>

        </div>{/* /inner container */}

        {/* ── MOBILE / TABLET MENU ─────────────── */}
        {mobileOpen && (
          <div
            style={{
              background: "#fff",
              borderTop: "1px solid rgba(144,81,15,0.10)",
              boxShadow: "0 12px 40px rgba(0,0,0,0.10)",
              animation: "mobileMenuIn 0.28s ease both",
              maxHeight: "calc(100dvh - 72px)",
              overflowY: "auto",
            }}
          >
            <ul
              style={{
                listStyle: "none",
                padding: "8px 16px 24px",
                margin: 0,
                display: "flex",
                flexDirection: "column",
              }}
            >

              {/* Home */}
              <MobileItem
                to="/"
                label={t("nav.home")}
                active={isActive("/")}
                onClick={() => setMobileOpen(false)}
              />

              {/* Services accordion */}
              <li style={{ borderBottom: "1px solid rgba(0,0,0,0.06)" }}>
                <button
                  onClick={() => setServicesOpen(v => !v)}
                  style={{
                    ...MOBILE_LINK,
                    width: "100%",
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "14px 0",
                    color: isActive("/service") ? BRAND : DARK,
                  }}
                >
                  <span>{t("nav.services")}</span>
                  <span
                    style={{
                      fontSize: "18px",
                      color: BRAND,
                      lineHeight: 1,
                      transition: "transform 0.25s",
                      transform: servicesOpen ? "rotate(45deg)" : "none",
                      display: "inline-block",
                    }}
                  >+</span>
                </button>
                {servicesOpen && (
                  <ul
                    style={{
                      listStyle: "none",
                      padding: "0 0 12px 16px",
                      margin: 0,
                      display: "flex",
                      flexDirection: "column",
                      gap: "8px",
                      borderLeft: "2px solid rgba(144,81,15,0.18)",
                      animation: "mobileMenuIn 0.22s ease both",
                    }}
                  >
                    {serviceSubLinks.map(({ to, label }) => (
                      <li key={to}>
                        <Link
                          to={to}
                          style={{
                            ...MOBILE_SUB_LINK,
                            color: isActive(to) ? BRAND : WARM,
                          }}
                          onClick={() => {
                            setMobileOpen(false);
                            setServicesOpen(false);
                          }}
                        >
                          {label}
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </li>

              {/* Remaining mobile links (index 1 onwards - Home already rendered) */}
              {mobileLinks.slice(1).map(({ to, label, sub }) => (
                sub ? (
                  /* Educations accordion, same pattern as Services above */
                  <li key={to} style={{ borderBottom: "1px solid rgba(0,0,0,0.06)" }}>
                    <button
                      onClick={() => setEduOpen(v => !v)}
                      style={{
                        ...MOBILE_LINK,
                        width: "100%",
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "14px 0",
                        color: sub.some(s => isActive(s.to)) ? BRAND : DARK,
                      }}
                    >
                      <span>{label}</span>
                      <span
                        style={{
                          fontSize: "18px",
                          color: BRAND,
                          lineHeight: 1,
                          transition: "transform 0.25s",
                          transform: eduOpen ? "rotate(45deg)" : "none",
                          display: "inline-block",
                        }}
                      >+</span>
                    </button>
                    {eduOpen && (
                      <ul
                        style={{
                          listStyle: "none",
                          padding: "0 0 12px 16px",
                          margin: 0,
                          display: "flex",
                          flexDirection: "column",
                          gap: "8px",
                          borderLeft: "2px solid rgba(144,81,15,0.18)",
                          animation: "mobileMenuIn 0.22s ease both",
                        }}
                      >
                        {sub.map(({ to: subTo, label: subLabel }) => (
                          <li key={subTo}>
                            <Link
                              to={subTo}
                              style={{
                                ...MOBILE_SUB_LINK,
                                color: isActive(subTo) ? BRAND : WARM,
                              }}
                              onClick={() => {
                                setMobileOpen(false);
                                setEduOpen(false);
                              }}
                            >
                              {subLabel}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                ) : (
                  <MobileItem
                    key={to}
                    to={to}
                    label={label}
                    active={isActive(to)}
                    onClick={() => setMobileOpen(false)}
                  />
                )
              ))}

              {/* Online Courses - coming soon */}
              <li style={{ borderBottom: "1px solid rgba(0,0,0,0.06)" }}>
                <span
                  style={{
                    ...MOBILE_LINK,
                    display: "flex",
                    alignItems: "center",
                    gap: "9px",
                    padding: "14px 0",
                    cursor: "default",
                    color: MUTED,
                  }}
                >
                  Online Courses
                  <span style={SOON_BADGE}>Soon</span>
                </span>
              </li>

              {/* Mobile CTA */}
              <li style={{ paddingTop: "20px" }}>
                <Link
                  to="/contact"
                  onClick={() => setMobileOpen(false)}
                  style={{
                    fontFamily: "'Inter','Roboto',sans-serif",
                    fontSize: "13px",
                    fontWeight: "700",
                    letterSpacing: "0.12em",
                    textTransform: "uppercase",
                    background: BRAND,
                    color: "#fff",
                    padding: "16px 28px",
                    borderRadius: "9999px",
                    textDecoration: "none",
                    display: "block",
                    textAlign: "center",
                    boxShadow: "0 6px 20px rgba(144,81,15,0.30)",
                  }}
                >
                  {t("nav.bookAppointment")}
                </Link>
              </li>

            </ul>
          </div>
        )}

      </header>
    </>
  );
};

/* ── Mobile list-item helper ── */
const MobileItem = ({ to, label, active, onClick }) => (
  <li style={{ borderBottom: "1px solid rgba(0,0,0,0.06)" }}>
    <Link
      to={to}
      style={{
        ...MOBILE_LINK,
        display: "block",
        padding: "14px 0",
        color: active ? BRAND : DARK,
      }}
      onClick={onClick}
    >
      {label}
    </Link>
  </li>
);

export default Main;
